import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Wrench, X, AlertTriangle, Loader2 } from 'lucide-react'
import { strapiPost } from '@/lib/strapi'
import { db } from '@/lib/db/database'
import { toast } from 'sonner'

function useDebugVisible() {
  if (typeof window === 'undefined') return false
  return new URLSearchParams(window.location.search).get('debug') === 'on'
}

type Action = 'bookings' | 'customers' | 'autoLayouts' | 'posOrders' | 'posOrderHistory' | 'menu' | 'customerOrders' | null

const ACTION_CONFIG: Record<NonNullable<Action>, { label: string; desc: string; endpoint: string; invalidateKeys: string[]; clearDexie?: () => Promise<void> }> = {
  bookings: {
    label: 'Delete All Bookings',
    desc: 'Permanently deletes every booking for your restaurant. This cannot be undone.',
    endpoint: '/api/restaurants/me/debug/clear-bookings',
    invalidateKeys: ['bookings'],
    clearDexie: () => db.bookings.clear(),
  },
  customers: {
    label: 'Delete All Customers',
    desc: 'Permanently deletes every customer record for your restaurant.',
    endpoint: '/api/restaurants/me/debug/clear-customers',
    invalidateKeys: ['customers'],
  },
  autoLayouts: {
    label: 'Clear Auto-Generated Layouts',
    desc: 'Deletes all auto-generated floor-plan layouts, keeping only your manually saved ones.',
    endpoint: '/api/restaurants/me/debug/clear-auto-layouts',
    invalidateKeys: ['floor-plan-layouts'],
  },
  posOrders: {
    label: 'Void All Open POS Orders',
    desc: 'Voids every active order (open / in-progress / paid) so all tables show Free. Menu is kept.',
    endpoint: '/api/restaurants/me/debug/clear-pos-orders',
    invalidateKeys: ['pos-orders'],
  },
  posOrderHistory: {
    label: 'Clear All POS Order History',
    desc: 'Permanently deletes every POS order (open, closed, voided) and clears local cache. Menu is kept.',
    endpoint: '/api/restaurants/me/debug/clear-pos-order-history',
    invalidateKeys: ['pos-orders'],
    clearDexie: () => db.posOrders.clear(),
  },
  menu: {
    label: 'Clear All Menu Items',
    desc: 'Permanently deletes every menu item, variant, modifier, and category. Past order line names will be blank.',
    endpoint: '/api/restaurants/me/debug/clear-menu',
    invalidateKeys: ['menu-items', 'menu-categories'],
    clearDexie: async () => {
      await db.menuItems.clear()
      await db.menuCategories.clear()
    },
  },
  customerOrders: {
    label: 'Clear All Customer Orders',
    desc: 'Permanently deletes every collection / delivery order and its pre-order items.',
    endpoint: '/api/restaurants/me/debug/clear-customer-orders',
    invalidateKeys: ['customer-orders', 'pre-order-items'],
    clearDexie: async () => {
      await db.customerOrders.clear()
      await db.preOrderItems.clear()
    },
  },
}

export function DebugPanel() {
  const visible = useDebugVisible()
  const qc = useQueryClient()

  const [open, setOpen] = useState(false)
  const [confirmAction, setConfirmAction] = useState<Action>(null)
  const [loading, setLoading] = useState(false)

  if (!visible) return null

  const handleConfirm = async () => {
    if (!confirmAction) return
    const cfg = ACTION_CONFIG[confirmAction]
    setLoading(true)
    try {
      const result = await strapiPost<{ deleted: number }>(cfg.endpoint, {})
      const deleted = (result as any)?.deleted ?? '?'
      toast.success(`${cfg.label} — ${deleted} record(s) deleted`)
      if (cfg.clearDexie) await cfg.clearDexie()
      for (const key of cfg.invalidateKeys) {
        qc.invalidateQueries({ queryKey: [key] })
      }
      setConfirmAction(null)
    } catch {
      toast.error(`Failed: ${cfg.label}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      {/* Floating trigger */}
      <button
        onClick={() => setOpen(true)}
        style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999 }}
        className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-zinc-800 border border-zinc-600 text-zinc-300 text-xs font-medium shadow-lg hover:bg-zinc-700 transition-colors"
        title="Debug panel (?debug=on)"
      >
        <Wrench className="h-3.5 w-3.5" />
        Debug
      </button>

      {/* Modal backdrop */}
      {open && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 10000 }}
          className="flex items-center justify-center bg-black/60"
          onClick={() => { if (!loading) setOpen(false) }}
        >
          <div
            className="w-full max-w-sm mx-4 rounded-xl bg-[var(--color-bg-surface)] border border-[var(--color-border-subtle)] shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-[var(--color-border-subtle)]">
              <div className="flex items-center gap-2 text-red-400">
                <AlertTriangle className="h-4 w-4" />
                <span className="font-semibold text-sm">Danger Zone</span>
              </div>
              <button
                onClick={() => { if (!loading) setOpen(false) }}
                className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Actions */}
            <div className="p-4 space-y-3">
              {(Object.entries(ACTION_CONFIG) as [NonNullable<Action>, typeof ACTION_CONFIG[NonNullable<Action>]][]).map(([key, cfg]) => (
                <div key={key} className="rounded-lg border border-[var(--color-border-subtle)] p-3">
                  <p className="text-sm font-medium text-[var(--color-text-primary)] mb-0.5">{cfg.label}</p>
                  <p className="text-xs text-[var(--color-text-muted)] mb-3">{cfg.desc}</p>

                  {confirmAction === key ? (
                    <div className="flex gap-2">
                      <button
                        onClick={handleConfirm}
                        disabled={loading}
                        className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-red-500/20 border border-red-500/40 text-red-400 text-xs font-medium hover:bg-red-500/30 transition-colors disabled:opacity-50"
                      >
                        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                        {loading ? 'Deleting…' : 'Yes, delete'}
                      </button>
                      <button
                        onClick={() => setConfirmAction(null)}
                        disabled={loading}
                        className="px-3 py-1.5 rounded-lg bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-[var(--color-text-muted)] text-xs hover:text-[var(--color-text-primary)] transition-colors disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmAction(key)}
                      className="w-full py-1.5 rounded-lg bg-[var(--color-bg-elevated)] border border-red-500/25 text-red-400/80 text-xs font-medium hover:bg-red-500/10 hover:border-red-500/40 transition-colors"
                    >
                      {cfg.label}
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="px-4 pb-4">
              <p className="text-[10px] text-[var(--color-text-muted)] text-center">
                Visible only when <code className="font-mono">?debug=on</code> is in the URL
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
